import type { FutebolJogoDia, FutebolVotacaoJogo } from '@/shared/types';

const BLACKLIST = ['goiás', 'vila nova'] as const;

const YOUTH_RE = /\b(u\s?\d{2}|sub[\s-]?\d{2}|under[\s-]?\d{2}|juveni[l]?|juniores?)\b/i;

// Nomes conferidos diretamente na BSD /api/v2/leagues/.
// Usar substrings para tolerar variações de temporada (ex: "World Cup 2026" → "world cup").
// Entradas mais longas/específicas DEVEM vir antes das mais curtas (ex: "de clubes" antes de "copa do mundo").
const LIGA_PESO: Record<string, number> = {
  'club world cup':          9,   // BSD (EN): "FIFA Club World Cup" — antes de 'world cup' (substring)
  'copa do mundo de clubes': 9,   // BSD (PT): "Copa do Mundo de Clubes" — antes de 'copa do mundo'
  'world cup':              10,   // BSD (EN): "World Cup 2026"
  'copa do mundo':          10,   // BSD (PT): "Copa do Mundo" — verificado 2026-06-12
  'champions league':    9,   // BSD: "Champions League"
  'copa do brasil':      8,   // BSD: "Copa do Brasil" ✓
  'copa libertadores':   8,   // BSD: "Copa Libertadores"
  'copa america':        7,
  'copa américa':        7,
  'int. friendly':       7,   // BSD: "Int. Friendly Games 2026"
  'international friendly': 7,
  'brasileirão serie a': 6,   // BSD: "Brasileirão Serie A" (sem acento em 'e')
  'brasileirao serie a': 6,
  'brasileirão betano':  6,
  'europa league':       6,   // BSD: "Europa League"
  'copa sudamericana':   5,   // BSD: "Copa Sudamericana"
  'brasileirão serie b': 4,   // BSD: "Brasileirão Serie B"
  'brasileirao serie b': 4,
  'saudi pro league':    3,
};

function isBlacklisted(time: string): boolean {
  return BLACKLIST.some((b) => time.toLowerCase().includes(b));
}

function isYouth(jogo: FutebolJogoDia): boolean {
  return (
    YOUTH_RE.test(jogo.time_casa) ||
    YOUTH_RE.test(jogo.time_visitante) ||
    YOUTH_RE.test(jogo.liga_nome ?? '')
  );
}

function pesoDaLiga(liga: string | null): number {
  if (!liga) return 1;
  const lower = liga.toLowerCase();
  for (const [chave, peso] of Object.entries(LIGA_PESO)) {
    if (lower.includes(chave)) return peso;
  }
  return 2;
}

const JANELA_MS = 30 * 60 * 1000; // 30 minutos

/**
 * Encontra o grupo de jogos cujos horários ficam dentro de uma janela de
 * 30 min entre si e que maximiza a soma de peso das ligas (top 3).
 */
function melhorJanela(jogos: FutebolJogoDia[]): FutebolJogoDia[] {
  if (jogos.length === 0) return [];

  const sorted = [...jogos].sort(
    (a, b) => new Date(a.horario).getTime() - new Date(b.horario).getTime(),
  );

  let melhorGrupo: FutebolJogoDia[] = [];
  let melhorPeso = -1;

  for (const ancora of sorted) {
    const tAncora = new Date(ancora.horario).getTime();
    const grupo   = sorted.filter(
      (j) => new Date(j.horario).getTime() - tAncora <= JANELA_MS,
    );
    const peso = grupo
      .slice()
      .sort((a, b) => pesoDaLiga(b.liga_nome) - pesoDaLiga(a.liga_nome))
      .slice(0, 3)
      .reduce((acc, j) => acc + pesoDaLiga(j.liga_nome), 0);

    if (peso > melhorPeso) {
      melhorPeso  = peso;
      melhorGrupo = grupo;
    }
  }

  return melhorGrupo;
}

/**
 * Filtra candidatos à votação e retorna até `max` jogos:
 *  1. Remove blacklist (Goiás, Vila Nova)
 *  2. Remove times/torneios de base (U20, Sub-17, etc.)
 *  3. Exige transmissão em canal brasileiro (televisionado_br) — desativável para seleção manual
 *  4. Agrupa por janela de 30 min e escolhe o grupo de maior peso
 *  5. Ordena por liga e horário dentro do grupo
 */
export function selecionarCandidatos(
  jogos: FutebolJogoDia[],
  max   = 3,
  { exigirTv = true }: { exigirTv?: boolean } = {},
): FutebolJogoDia[] {
  const qualificados = jogos.filter(
    (j) =>
      !isBlacklisted(j.time_casa) &&
      !isBlacklisted(j.time_visitante) &&
      !isYouth(j) &&
      (!exigirTv || j.televisionado_br === true),
  );

  return melhorJanela(qualificados)
    .sort((a, b) => {
      const diff = pesoDaLiga(b.liga_nome) - pesoDaLiga(a.liga_nome);
      return diff !== 0 ? diff : new Date(a.horario).getTime() - new Date(b.horario).getTime();
    })
    .slice(0, max);
}

/**
 * Retorna todos os grupos de votação do dia, ordenados por horário crescente.
 * Cada grupo representa um ciclo independente de votação (janela de 30 min).
 * Jogos que caem na mesma janela de 30 min são agrupados juntos (top 3 por peso).
 *
 * exigirTv=false: votação automática — inclui jogos mesmo sem flag televisionado_br,
 * evitando que falhas na sync de broadcasts bloqueiem toda a votação.
 */
export function listarGruposDeVotacao(
  jogos: FutebolJogoDia[],
  max   = 3,
  { exigirTv = false }: { exigirTv?: boolean } = {},
): FutebolJogoDia[][] {
  const qualificados = jogos.filter(
    (j) =>
      !isBlacklisted(j.time_casa) &&
      !isBlacklisted(j.time_visitante) &&
      !isYouth(j) &&
      (!exigirTv || j.televisionado_br === true),
  );

  if (qualificados.length === 0) return [];

  const sorted = [...qualificados].sort(
    (a, b) => new Date(a.horario).getTime() - new Date(b.horario).getTime(),
  );

  // Agrupa: cada jogo que não pertence à janela do grupo anterior inicia novo grupo
  const grupos: FutebolJogoDia[][] = [];
  let grupoAtual: FutebolJogoDia[] = [];
  let tAncora = -Infinity;

  for (const jogo of sorted) {
    const t = new Date(jogo.horario).getTime();
    if (grupoAtual.length === 0 || t - tAncora <= JANELA_MS) {
      if (grupoAtual.length === 0) tAncora = t;
      grupoAtual.push(jogo);
    } else {
      grupos.push(grupoAtual);
      grupoAtual = [jogo];
      tAncora    = t;
    }
  }
  if (grupoAtual.length > 0) grupos.push(grupoAtual);

  // Dentro de cada grupo: top max por peso de liga, desempate por horário
  return grupos.map((g) =>
    g
      .sort((a, b) => {
        const diff = pesoDaLiga(b.liga_nome) - pesoDaLiga(a.liga_nome);
        return diff !== 0 ? diff : new Date(a.horario).getTime() - new Date(b.horario).getTime();
      })
      .slice(0, max),
  );
}

/**
 * Escolhe o vencedor a anunciar dentre os `winners` acumulados do dia.
 *
 * `encerrar_votacao_ciclo` marca `vencedor=true` no jogo eleito mas nunca apaga
 * vencedores de ciclos anteriores (podem existir vários por dia, um por horário).
 * Escolher por maior número de votos (`winners[0]`) pode reexibir o vencedor
 * de um ciclo antigo indefinidamente — trava o card de anúncio e ele nunca bate
 * com o jogo realmente ativo. `ultimoCicloFixtureIds` é o conjunto de fixture_ids
 * do último grupo de candidatos ativos visto pelo client (antes de zerar) —
 * identifica com segurança o vencedor do ciclo que acabou de fechar.
 *
 * Fallback: se o hook nunca observou a transição ao vivo (ex: app aberto depois
 * do ciclo já ter fechado) e existe só UM vencedor acumulado, não há ambiguidade
 * — mostra ele. Com 2+ vencedores acumulados e nenhuma transição observada, não
 * dá pra saber qual é o mais recente só com os dados da linha — não mostra nada
 * em vez de arriscar mostrar um vencedor errado.
 */
/**
 * Próximo valor de `ultimoCicloFixtureIds` a partir dos candidatos ativos.
 *
 * encerrar_votacao_ciclo faz UPDATE vencedor=true ANTES do DELETE dos perdedores,
 * e o CDC entrega nessa ordem: no render intermediário `ativos` já não tem o
 * vencedor. Se os ativos só encolheram (subconjunto do ciclo memorizado),
 * mantém o conjunto anterior — que inclui o vencedor; um conjunto novo
 * (próximo ciclo) substitui.
 */
export function atualizarIdsUltimoCiclo(anteriores: number[], ativosIds: number[]): number[] {
  if (ativosIds.length === 0) return anteriores;
  const encolheu = anteriores.length > 0 && ativosIds.every((id) => anteriores.includes(id));
  return encolheu ? anteriores : ativosIds;
}

export function selecionarVencedorAtivo(
  winners: FutebolVotacaoJogo[],
  ultimoCicloFixtureIds: number[],
): FutebolVotacaoJogo | null {
  const doUltimoCiclo = winners.find((w) => ultimoCicloFixtureIds.includes(w.fixture_id));
  if (doUltimoCiclo) return doUltimoCiclo;
  return winners.length === 1 ? winners[0] : null;
}
